import FileUploadIcon from "@mui/icons-material/FileUpload";
import LoadingButton from "@mui/lab/LoadingButton";
import { Grid } from "@mui/material";
import { t } from "i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useSnackbarAlert } from "../../../context/snackbarAlert";
import {
    DOCS_RESTRICTED_ROLE,
    DOCS_RESTRICTED_USER,
    MAX_ALLOWED_ENCLOSURE_SIZE_IN_BYTES,
    MAX_FILENAME_LENGTH_CHARS,
} from "../../../helpers/constants";
import { getRandomString } from "../../../helpers/methods";
import { useAxiosPrivate } from "../../../hooks/useAxiosPrivate";
import { useForm } from "../../../hooks/useForm";
import {
    documentsApi,
    useDeleteDocumentFromAgreement,
    useDeleteDocumentFromAllInvests,
    useDeleteDocumentFromAllProducts,
    useDeleteDocumentFromCustomer,
    useDeleteDocumentFromChannel,
    useDocsFilteringData,
    useUploadFilesForAgreement,
    useUploadFilesForCustomer,
    useUploadFilesForInvests,
    useUploadFilesForProducts,
    useUploadFilesForChannel,
    useUploadOnlyFiles,
} from "../../../services/documentsService";
import BasicDialog from "../../base/BasicDialog";
import BoxWithLabel from "../../box/BoxWithLabel/BoxWithLabel";
import AutocompleteField from "../../field/AutocompleteField";
import TextFieldFormControl from "../../field/TextFieldFormControl";
import AddFileForm from "../../form/AddFileForm/AddFileForm";
import DocsRestrictedCardTypes from "../../other/DocsRestrictedCardTypes";
import LoaderWrapper from "../../wrapper/LoaderWrapper";
import { useSingleDocumentDetails, useUpdateDocument } from "../../../services/documentsService";
import { isEmptyValue, isEmptyArray } from "../../../helpers/methods";

const DOCS_RESTRICTED_TYPES_CONFIG = [
    {
        id: DOCS_RESTRICTED_ROLE,
        label: t("dialog.file_dialog.docs_restricted_role"),
        icon: "EngineeringIcon",
    },
    {
        id: DOCS_RESTRICTED_USER,
        label: t("dialog.file_dialog.docs_restricted_user"),
        icon: "GroupsIcon",
    },
];

const AddEditDocsRestrictedDialog = (props) => {
    const { t } = useTranslation();
    const snackbarAlert = useSnackbarAlert();
    const axiosPrivate = useAxiosPrivate();

    const isRevisionMode = props.isRevisionMode === true;
    const isEditMode = props.docsId != null && !isRevisionMode;

    const relatedModelType = useMemo(() => {
        if (props.agreement_id) return "agreement"
        if (props.channel_id) return "channel"
        if (props.invest_ids) return "invest"
        if (props.product_ids) return "product"

        return undefined;
    }, [props.agreement_id, props.channel_id, props.invest_ids, props.product_ids]);

    const { data: filteringData, isLoading: isFilteringDataLoading } = useDocsFilteringData();
    const { data: editDocsData, isLoading: isEditDocsDataLoading } = useSingleDocumentDetails(isEditMode ? props.docsId : null, relatedModelType);
    const { mutate: updateDocument, isPending: isUpdateDocumentPending} = useUpdateDocument({
        onSuccess: () => {
            toast.success(t("snackbar_alert.file_updated_successfully"));
            if (props.returnDocsNoteOnSumbit) {
                props.onSubmit?.([props.docsId], formValue?.note)
            } else {
                props.onSubmit?.([]);
            }
            
        },
        onError: () => {
            toast.error(t("snackbar_alert.occurred_error_during_file_updating"));
        },
    })

    const { formValue, setFormValue, onChange, onChangeAutocompleteFieldWithObjectOptions } =
        useForm({});

    useEffect(() => {
        const initForm = {
            docs_restricted_type: filteringData?.sales_default_roles
                ? DOCS_RESTRICTED_ROLE
                : undefined,
            docs_restricted_role: filteringData?.sales_default_roles ?? [],
        };
        if (!isFilteringDataLoading && !isEditMode) {
            setFormValue((prev) => ({ ...prev, ...initForm }));
        }

        if (isEditMode && !isEditDocsDataLoading) {
            // get note from frontend if note is storage localy
            setFormValue({...editDocsData, note: props.docsNote || editDocsData.note, docs_restricted_type: getInitialDocsRestrictedTypeValue(editDocsData)})
            setSelectedFiles([
                { 
                    docs_name: editDocsData.docs_name, 
                    docs_content_type: editDocsData.docs_content_type, 
                    id: editDocsData.id, 
                    miniature_download_link: editDocsData.miniature_download_link
                },
            ]);
        }
    }, [isFilteringDataLoading, isEditMode, isEditDocsDataLoading]);

    const [selectedFiles, setSelectedFiles] = useState([]);

    const deleteForCustomerMutation = useDeleteDocumentFromCustomer({
        onSuccess: () => {
            toast.success(t("snackbar_alert.file_deleted_successfully"));
            props.onSubmit?.([]);
        },
        onError: () => {
            toast.error(t("snackbar_alert.occurred_error_during_file_deleting"));
        },
    });

    const deleteForProductMutation = useDeleteDocumentFromAllProducts({
        onSuccess: () => {
            toast.success(t("snackbar_alert.file_deleted_successfully"));
            props.onSubmit?.([]);
        },
        onError: () => {
            toast.error(t("snackbar_alert.occurred_error_during_file_deleting"));
        },
    });

    const deleteForAgreementMutation = useDeleteDocumentFromAgreement({
        onSuccess: () => {
            toast.success(t("snackbar_alert.file_deleted_successfully"));
            props.onSubmit?.([]);
        },
        onError: () => {
            toast.error(t("snackbar_alert.occurred_error_during_file_deleting"));
        },
    });

    const deleteForInvestMutation = useDeleteDocumentFromAllInvests({
        onSuccess: () => {
            toast.success(t("snackbar_alert.file_deleted_successfully"));
            props.onSubmit?.([]);
        },
        onError: () => {
            toast.error(t("snackbar_alert.occurred_error_during_file_deleting"));
        },
    });

    const deleteForChannelMutation = useDeleteDocumentFromChannel({
        onSuccess: () => {
            toast.success(t("snackbar_alert.file_deleted_successfully"));
            props.onSubmit?.([]);
        },
        onError: () => {
            toast.error(t("snackbar_alert.occurred_error_during_file_deleting"));
        },
    });

    const onUploadSuccess = (createdDocIds) => {
        toast.success(t("snackbar_alert.file_uploaded_successfully"));
        if (isRevisionMode && props.docsId) {
            if (props.customer_id) {
                deleteForCustomerMutation.mutate({
                    document_id: props.docsId,
                    customer_id: props.customer_id,
                });
            } else if (props.agreement_id) {
                deleteForAgreementMutation.mutate({
                    document_id: props.docsId,
                    agreement_id: props.agreement_id,
                });
            } else if (props.invest_ids) {
                deleteForInvestMutation.mutate({
                    document_id: props.docsId,
                });
            } else if (props.product_ids && props.product_ids.length > 0) {
                deleteForProductMutation.mutate({
                    document_id: props.docsId,
                });
            }
            else if (props.channel_id) {
                deleteForChannelMutation.mutate({
                    document_id: props.docsId,
                    channel_id: props.channel_id,
                });
            }
        }
        if (props.returnDocsNoteOnSumbit) {
            props.onSubmit?.(createdDocIds, formValue?.note)
        } else {
            props.onSubmit?.(createdDocIds);
        }
        props.onClose();
    };

    const onUploadError = () => {
        toast.error(t("snackbar_alert.occurred_error_during_upload"));
    };

    const uploadForProductMutation = useUploadFilesForProducts({
        onSuccess: onUploadSuccess,
        onError: onUploadError,
    });

    const uploadForCustomerMutation = useUploadFilesForCustomer({
        onSuccess: onUploadSuccess,
        onError: onUploadError,
    });

    const uploadForAgreementMutation = useUploadFilesForAgreement({
        onSuccess: onUploadSuccess,
        onError: onUploadError,
    });

    const uploadForInvestMutation = useUploadFilesForInvests({
        onSuccess: onUploadSuccess,
        onError: onUploadError,
    });

    const uploadForChannelMutation = useUploadFilesForChannel({
        onSuccess: onUploadSuccess,
        onError: onUploadError,
    });

    const uploadOnlyFiles = useUploadOnlyFiles({
        onSuccess: onUploadSuccess,
        onError: onUploadError,
    });

    const selectedUploadMutation = 
        props.customer_id ? uploadForCustomerMutation :
        props.agreement_id ? uploadForAgreementMutation :
        props.channel_id ? uploadForChannelMutation :
        props.invest_ids ? uploadForInvestMutation :
        props.product_ids ? uploadForProductMutation : uploadOnlyFiles;
    
    const getDialogTitle = () => {
        if (isEditMode) {
            if (props.readOnly) {
                return t("dialog.file_dialog.docs_details");
            }
            return t("dialog.file_dialog.edit_docs");
        }
        if (isRevisionMode) {
            return t("dialog.file_dialog.revised_docs");
        }
        return t("dialog.file_dialog.add_docs");
    };

    const onSubmit = async () => {
        if (isEditMode) {
            return updateDocument({id:props.docsId, data: {...formValue, docs_type: relatedModelType}})
        }
        
        
        if (selectedFiles.length === 0) {
            toast.error(t("dialog.file_dialog.no_file_selected"));
            return;
        }

        const baseData = {
            ...formValue,
            revision_parent: isRevisionMode ? props.docsId : undefined,
        };

        let commonData;

        if (
            formValue.docs_restricted_type === DOCS_RESTRICTED_USER &&
            formValue.docs_restricted_user &&
            Array.isArray(formValue.docs_restricted_user) &&
            formValue.docs_restricted_user.length > 0 &&
            formValue.docs_restricted_user.some((user) => user !== null && user !== undefined)
        ) {
            const { docs_restricted_role, ...dataWithoutRole } = baseData;
            commonData = {
                ...dataWithoutRole,
                docs_restricted_user: formValue.docs_restricted_user.filter(
                    (user) => user !== null && user !== undefined,
                ),
            };
        } else if (
            formValue.docs_restricted_type === DOCS_RESTRICTED_ROLE &&
            formValue.docs_restricted_role &&
            Array.isArray(formValue.docs_restricted_role) &&
            formValue.docs_restricted_role.length > 0 &&
            formValue.docs_restricted_role.some((role) => role !== null && role !== undefined)
        ) {
            const { docs_restricted_user, ...dataWithoutUser } = baseData;
            commonData = {
                ...dataWithoutUser,
                docs_restricted_role: formValue.docs_restricted_role.filter(
                    (role) => role !== null && role !== undefined,
                ),
            };
        } else {
            const {
                docs_restricted_user,
                docs_restricted_role,
                docs_restricted_type,
                ...cleanData
            } = baseData;
            commonData = cleanData;
        }
        commonData = {
            ...commonData,
            is_card_jpg: props.isCardJpg,
            is_card_pdf: props.isCardPdf,
            is_prospectus: props.isProspectus,
        };

        // Add flags to commonData to create cards, investment_prospecuts
        commonData = {
            ...commonData,
            is_card_jpg: props.isCardJpg,
            is_card_pdf: props.isCardPdf,
            is_prospectus: props.isProspectus,
        };

        const mutationPayload = {
            files: selectedFiles.map((sf) => ({
                file: sf.file,
                docs_name: sf.docs_name,
                unique_index: sf.unique_index,
                revision_parent: isRevisionMode ? props.docsId : undefined,
            })),
            commonData: {
                ...commonData,
                revision_parent: isRevisionMode ? props.docsId : undefined,
                is_card_jpg: props.isCardJpg,
                is_card_pdf: props.isCardPdf,
                is_prospectus: props.isProspectus,
            },
        };

        if (props.customer_id) {
            uploadForCustomerMutation.mutate({
                ...mutationPayload,
                customer_id: props.customer_id,
            });
        } else if (props.agreement_id) {
            uploadForAgreementMutation.mutate({
                ...mutationPayload,
                agreement_id: props.agreement_id,
            });
        } else if (props.invest_ids) {
            uploadForInvestMutation.mutate({
                ...mutationPayload,
                invest_ids: props.invest_ids,
            });
        } else if (props.channel_id) {
            uploadForChannelMutation.mutate({
                ...mutationPayload,
                channel_id: props.channel_id,
        });
        } else if (props.product_ids) {
            uploadForProductMutation.mutate({
                ...mutationPayload,
                product_ids: props.product_ids || [],
            });
        } else {
                uploadOnlyFiles.mutate({
                ...mutationPayload,
                // get docs_nr from frontend
                docs_nr: props.docs_nr
            });
        }
    };

    const handleChangeDocsRestrictedType = (restrictedValue) => {
        if (!props.readOnly) {
            setFormValue((prev) => ({
                ...prev,
                docs_restricted_type: restrictedValue,
                docs_restricted_user: [],
                docs_restricted_role: [],
            }));
        }
    };

    const getInitialDocsRestrictedTypeValue = (formValue) => {
        let fieldName = null;
        props.availableDocsRestrictedTypes.forEach((field) => {
        if (
            !isEmptyValue(formValue?.[field]) &&
            !isEmptyArray(formValue?.[field])
        )
            fieldName = field;
        });

        return fieldName;
    };

    const setNotAddedNames = (namesArr) => {
        let formattedNames = "";
        for (const name of namesArr) {
            formattedNames += `\n${name}`;
        }
        return (
            t("snackbar_alert.max_allowed_file_size_or_name_is_exceeded_files_are_not_added") +
            formattedNames
        );
    };

    const onChooseFile = (e) => {
        const selectedFilesList = e.target.files;
        const enclosures = [];
        const notAddedFilesNames = [];

        if (selectedFilesList) {
            for (const file of Array.from(selectedFilesList)) {
                if (
                    file.size < MAX_ALLOWED_ENCLOSURE_SIZE_IN_BYTES &&
                    file.name.length < MAX_FILENAME_LENGTH_CHARS
                ) {
                    const enclosure = {
                        file: file,
                        docs_name: file.name,
                        content_type: file.type,
                        blob_url: window.URL.createObjectURL(new Blob([file], { type: file.type })),
                        unique_index: getRandomString(),
                    };
                    enclosures.push(enclosure);
                } else {
                    notAddedFilesNames.push(file.name);
                }
            }

            setSelectedFiles((prevFiles) => {
                if (props.multiple) {
                    return [...prevFiles, ...enclosures];
                }
                return [...enclosures];
            });

            if (notAddedFilesNames.length > 0) {
                toast.error(setNotAddedNames(notAddedFilesNames));
            }
        }
    };

    const onPreviewDocs = useCallback(
        async (itemId, index) => {
            if (itemId) {
                try {
                    await documentsApi.handlePreviewDocumentFromLink(axiosPrivate, itemId);
                } catch (error) {
                    toast.error(t("snackbar_alert.occurred_error_during_preview"));
                }
            } else if (index !== undefined) {
                const file = selectedFiles[index];
                window.open(file.blob_url, "_blank");
            }
        },
        [selectedFiles, axiosPrivate, t],
    );

    const onDeleteSelectedDocs = (index) => {
        if (props.onDeleteFile) {
            props.onDeleteFile()
        }
        else {
            setSelectedFiles((prevFiles) => {
                const newFiles = [...prevFiles];
                newFiles.splice(index, 1);
                return newFiles;
            });
        }
    };

    const filesAcceptTypes = useMemo(() => {
        if (props.isCardJpg) {
            return ".jpg";
        }

        if (props.isCardPdf || props.isProspectus) {
            return ".pdf";
        }

        return undefined;
    }, [props.isCardJpg, props.isCardPdf, props.isProspectus]);

    const isFormValid = selectedFiles.length > 0;

    return (
        <BasicDialog
            open={props.open}
            onClose={() => {
                props.onClose();
                setFormValue({});
            }}
            titleAlign="center"
            contentAlign="center"
            title={getDialogTitle()}
            maxWidth="md"
        >
            <LoaderWrapper showLoader={selectedUploadMutation.isPending || isFilteringDataLoading || isEditDocsDataLoading}>
                <Grid
                    container
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="center"
                    spacing={1}
                >
                    <Grid item xs={12}>
                        <AddFileForm
                            // @ts-ignore
                            docs={selectedFiles}
                            onChooseFile={onChooseFile}
                            onDeleteFile={onDeleteSelectedDocs}
                            onPreview={onPreviewDocs}
                            fileType="docs"
                            multiple={props.multiple || false}
                            showButtonWithFileRestriction={
                                !isEditMode &&
                                (props.multiple ? props.multiple : selectedFiles.length === 0)
                            }
                            readOnly={props.readOnly || false}
                            accept={filesAcceptTypes}
                            showDownloadButton={props.showDownloadButton}
                            onDownload={props.onDownload}
                            canRemoveFiles={props.canRemoveFiles}
                        />
                    </Grid>

                    {(props.availableDocsRestrictedTypes?.length || 0) > 0 && !isRevisionMode && (
                        <>
                            <Grid item xs={12}>
                                <BoxWithLabel
                                    border="0px solid white"
                                    boxStyle={{ padding: 0, margin: -4 }}
                                    label={t("dialog.file_dialog.docs_restricted_type")}
                                />
                                <DocsRestrictedCardTypes
                                    // @ts-ignore
                                    restrictedTypes={DOCS_RESTRICTED_TYPES_CONFIG.filter(
                                        (config) =>
                                            props.availableDocsRestrictedTypes?.includes(
                                                config.id,
                                            ) || false,
                                    )}
                                    onCardClick={handleChangeDocsRestrictedType}
                                    selectedRestrictedTypeId={formValue?.docs_restricted_type}
                                />
                            </Grid>

                            {formValue?.docs_restricted_type === DOCS_RESTRICTED_USER && (
                                <Grid item xs={12}>
                                    <AutocompleteField
                                        name="docs_restricted_user"
                                        multiple={true}
                                        addNewValue={false}
                                        onChange={onChangeAutocompleteFieldWithObjectOptions}
                                        options={filteringData?.docs_restricted_user || []}
                                        label={t("dialog.file_dialog.docs_restricted_user")}
                                        isObjectOption
                                        optionLabelKey="full_name"
                                        value={formValue?.docs_restricted_user}
                                        disabled={props.readOnly}
                                    />
                                </Grid>
                            )}

                            {formValue?.docs_restricted_type === DOCS_RESTRICTED_ROLE && (
                                <Grid item xs={12}>
                                    <AutocompleteField
                                        name="docs_restricted_role"
                                        multiple={true}
                                        addNewValue={false}
                                        onChange={onChangeAutocompleteFieldWithObjectOptions}
                                        options={filteringData?.docs_restricted_role || []}
                                        label={t("dialog.file_dialog.docs_restricted_role")}
                                        isObjectOption
                                        optionLabelKey="role_name"
                                        value={formValue?.docs_restricted_role}
                                        disabled={props.readOnly}
                                    />
                                </Grid>
                            )}
                        </>
                    )}

                    {(isEditMode || props.showIndustry) && (
                        <Grid item sm={6} xs={12}>
                            <AutocompleteField
                                name="industry"
                                multiple={true}
                                onChange={onChangeAutocompleteFieldWithObjectOptions}
                                options={filteringData?.industries || []}
                                label={t("dialog.file_dialog.industry")}
                                isObjectOption
                                optionLabelKey="industry_name"
                                value={formValue?.industry}
                                disabled={props.readOnly}
                            />
                        </Grid>
                    )}

                    {props.showStandards && (
                        <Grid item sm={6} xs={12}>
                            <AutocompleteField
                                name="invest_stantard"
                                multiple={false}
                                onChange={onChangeAutocompleteFieldWithObjectOptions}
                                options={filteringData?.standards || []}
                                label={t("dialog.file_dialog.invest_stantard")}
                                isObjectOption
                                optionLabelKey="docs_investan_name"
                                value={formValue?.invest_stantard}
                                disabled={props.readOnly}
                            />
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <TextFieldFormControl
                            name="note"
                            label={t("dialog.file_dialog.docs_note")}
                            value={formValue?.note || ""}
                            onChange={onChange}
                            disabled={props.readOnly}
                            multiline
                            rows={2}
                        />
                    </Grid>

                    {!props.readOnly && (
                        <Grid item xs={12}>
                            <LoadingButton
                                disabled={!isFormValid || selectedUploadMutation.isPending || isUpdateDocumentPending}
                                variant="contained"
                                onClick={onSubmit}
                                component="label"
                                loading={selectedUploadMutation.isPending || isUpdateDocumentPending}
                                fullWidth
                                startIcon={<FileUploadIcon />}
                            >
                                {isEditMode
                                    ? t("dialog.file_dialog.update_file")
                                    : t("dialog.file_dialog.add_file")}
                            </LoadingButton>
                        </Grid>
                    )}
                </Grid>
            </LoaderWrapper>
        </BasicDialog>
    );
};

export default AddEditDocsRestrictedDialog;
